import { useReactFlow } from '@xyflow/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { WebhookNodeData } from '../../../converters/shared/reactFlowTypes'

/**
 * Hook for webhook node functionality.
 * Handles URL updates only.
 */
export const useWebhookNode = (id: string, data: WebhookNodeData) => {
  const { updateNodeData } = useReactFlow()
  const [url, setUrl] = useState(data.url || '')
  const isUpdatingRef = useRef(false)
  const dataRef = useRef(data)

  // Keep dataRef in sync with data
  useEffect(() => {
    dataRef.current = data
  }, [data])

  // Sync local state when node data changes from outside (not from our own updates)
  useEffect(() => {
    if (!isUpdatingRef.current) {
      const newUrl = data.url || ''
      if (newUrl !== url) {
        setUrl(newUrl)
      }
    }
    // Don't reset the flag here - let handleUrlChange manage it
  }, [data.url, url])

  const handleUrlChange = useCallback(
    (newUrl: string) => {
      // Only update local state - don't update React Flow yet
      // This prevents re-renders while typing
      isUpdatingRef.current = true
      setUrl(newUrl)
    },
    [id, updateNodeData, url],
  )

  const handleUrlBlur = useCallback(() => {
    // Update React Flow when user is done typing (on blur)
    updateNodeData(id, {
      ...dataRef.current,
      url: url || undefined,
    })
    // Reset flag after a delay
    isUpdatingRef.current = false
  }, [id, updateNodeData, url])

  return {
    currentUrl: url,
    handleUrlChange,
    handleUrlBlur,
    hasData: !!url,
  }
}
